"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQuery } from "convex/react";
import { Building2, CheckSquare, FolderKanban, Sparkles, X } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { MicButton } from "@/components/ui/MicButton";

type Tab = "task" | "project" | "client" | "ai";

type Priority = "low" | "medium" | "high" | "urgent";

const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];

const TABS: { key: Tab; label: string; icon: typeof CheckSquare }[] = [
  { key: "task", label: "Task", icon: CheckSquare },
  { key: "project", label: "Project", icon: FolderKanban },
  { key: "client", label: "Client", icon: Building2 },
  { key: "ai", label: "AI Request", icon: Sparkles },
];

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function NewItemModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>("task");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:p-8">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-10 mt-8 w-full max-w-xl rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3.5">
          <h2 className="text-base font-semibold text-foreground">Create new</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex border-b border-zinc-800 px-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors",
                  active
                    ? "border-violet-400 text-foreground"
                    : "border-transparent text-zinc-400 hover:text-zinc-100",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {tab === "task" && <TaskForm onDone={onClose} />}
          {tab === "project" && <ProjectForm onDone={onClose} />}
          {tab === "client" && <ClientForm onDone={onClose} />}
          {tab === "ai" && <AIRequestForm onDone={onClose} />}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-[10px] uppercase tracking-wider text-zinc-500">
      {children}
    </span>
  );
}

function NativeSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </select>
  );
}

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;
  return <div className="text-xs text-red-400">{message}</div>;
}

function TaskForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const team = useQuery(api.team.list);
  const projects = useQuery(api.projects.list);
  const createTask = useMutation(api.tasks.create);

  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState<string>("");
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      const id = await createTask({
        title: title.trim(),
        description: description.trim() || undefined,
        status: "inbox",
        priority,
        assigneeId: assigneeId ? (assigneeId as Id<"teamMembers">) : undefined,
        projectId: projectId ? (projectId as Id<"projects">) : undefined,
        dueDate: dueDate ? new Date(dueDate).getTime() : undefined,
      });
      onDone();
      router.push(`/tasks/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <FieldLabel>Title *</FieldLabel>
        <div className="relative flex items-center">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            autoFocus
            className="pr-8"
          />
          <MicButton
            onTranscript={(text) =>
              setTitle((prev) => (prev ? `${prev} ${text}` : text))
            }
            className="absolute right-1"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5">
          <FieldLabel>Project</FieldLabel>
          <NativeSelect value={projectId} onChange={setProjectId}>
            <option value="">No project</option>
            {projects?.map((project) => (
              <option key={project._id} value={project._id}>{project.title}</option>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Assignee</FieldLabel>
          <NativeSelect value={assigneeId} onChange={setAssigneeId}>
            <option value="">Unassigned</option>
            {team?.filter((m) => m.status !== "inactive").map((member) => (
              <option key={member._id} value={member._id}>{member.name}</option>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Priority</FieldLabel>
          <NativeSelect value={priority} onChange={(v) => setPriority(v as Priority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p} className="capitalize">{p}</option>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Due date</FieldLabel>
          <input
            type="date"
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-1.5">
        <FieldLabel>Description</FieldLabel>
        <div className="relative">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional context, success criteria, links."
            className="min-h-24 bg-zinc-950 pb-7"
          />
          <MicButton
            onTranscript={(text) =>
              setDescription((prev) => (prev ? `${prev} ${text}` : text))
            }
            className="absolute bottom-1.5 right-1.5"
          />
        </div>
      </div>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !title.trim()}>
          {submitting ? "Creating…" : "Create task"}
        </Button>
      </div>
    </form>
  );
}

function ProjectForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const clients = useQuery(api.clients.list);
  const createProject = useMutation(api.projects.create);

  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!clientId) {
      setError("Pick a client (or create one first)");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      const id = await createProject({
        title: title.trim(),
        description: description.trim() || undefined,
        status: "active",
        clientId: clientId as Id<"clients">,
      });
      onDone();
      router.push(`/projects/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create project");
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <label className="space-y-1.5">
        <FieldLabel>Title *</FieldLabel>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Project name"
          autoFocus
        />
      </label>

      <label className="space-y-1.5">
        <FieldLabel>Client *</FieldLabel>
        <NativeSelect value={clientId} onChange={setClientId}>
          <option value="">Select a client</option>
          {clients?.map((client) => (
            <option key={client._id} value={client._id}>{client.name}</option>
          ))}
        </NativeSelect>
      </label>

      <label className="space-y-1.5">
        <FieldLabel>Description</FieldLabel>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this project about?"
          className="min-h-24 bg-zinc-950"
        />
      </label>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !title.trim() || !clientId}>
          {submitting ? "Creating…" : "Create project"}
        </Button>
      </div>
    </form>
  );
}

function ClientForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const createClient = useMutation(api.clients.create);

  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [summary, setSummary] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      await createClient({
        slug: slugify(name),
        name: name.trim(),
        status: "active",
        type: "other",
        website: website.trim() || undefined,
        summary: summary.trim() || undefined,
      });
      onDone();
      router.push("/clients");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create client");
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <label className="space-y-1.5">
        <FieldLabel>Name *</FieldLabel>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Client name"
          autoFocus
        />
      </label>

      <label className="space-y-1.5">
        <FieldLabel>Website</FieldLabel>
        <Input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://…"
        />
      </label>

      <label className="space-y-1.5">
        <FieldLabel>Summary</FieldLabel>
        <Textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="One-liner about who they are and what they do."
          className="min-h-24 bg-zinc-950"
        />
      </label>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !name.trim()}>
          {submitting ? "Creating…" : "Create client"}
        </Button>
      </div>
    </form>
  );
}

function AIRequestForm({ onDone }: { onDone: () => void }) {
  const team = useQuery(api.team.list);
  const createTask = useMutation(api.tasks.create);

  const iris = useMemo(
    () => team?.find((m) => m.name.toLowerCase() === "iris"),
    [team],
  );

  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!text.trim()) {
      setError("Describe what you need");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      const trimmed = text.trim();
      await createTask({
        title: trimmed.slice(0, 60),
        description: trimmed,
        status: "inbox",
        priority: "medium",
        assigneeId: iris?._id,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-md border border-violet-900/40 bg-violet-950/20 px-3 py-3 text-sm text-violet-200">
          <Sparkles className="h-4 w-4" />
          Request submitted — Iris will pick it up shortly.
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={onDone}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <FieldLabel>Describe what you need…</FieldLabel>
        <div className="relative">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='e.g. "Set up keyword research for Risdall focused on manufacturing terms."'
            className="min-h-32 bg-zinc-950 pb-7"
            autoFocus
            disabled={submitting}
          />
          <MicButton
            onTranscript={(text) =>
              setText((prev) => (prev ? `${prev} ${text}` : text))
            }
            className="absolute bottom-1.5 right-1.5"
          />
        </div>
      </div>

      {!iris && team && (
        <div className="text-xs text-amber-400">
          Iris isn&apos;t set up as a team member yet — the task will be created unassigned.
        </div>
      )}

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !text.trim()}>
          {submitting ? "Submitting…" : "Submit Request"}
        </Button>
      </div>
    </form>
  );
}
